import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router';
import { canDecide, useSession } from '../auth/session';
import { Header } from '../components/Header';
import { EmptyState, Spinner, formatDate } from '../components/ui';
import { pair, tokens, type TokenInfo } from '../api/endpoints';
import { ApiError } from '../api/client';

const CODE_LENGTH = 8;

/** Keeps only alphanumerics the server would accept, uppercased, capped to CODE_LENGTH. */
function stripCode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, CODE_LENGTH);
}

/** Displays the raw code as XXXX-XXXX, the way the server prints it. */
function formatCode(raw: string): string {
  return raw.length > 4 ? `${raw.slice(0, 4)}-${raw.slice(4)}` : raw;
}

interface DeviceRowProps { t: TokenInfo; onRevoke: () => void }

function DeviceRow({ t, onRevoke }: DeviceRowProps) {
  return (
    <tr>
      <td>{t.label}</td>
      <td className="mono muted">{formatDate(t.createdAt)}</td>
      <td className="mono muted">{t.lastUsedAt ? formatDate(t.lastUsedAt) : 'Never'}</td>
      <td className="mono muted">{t.expiresAt ? formatDate(t.expiresAt) : ''}</td>
      <td><button className="btn sm danger" onClick={onRevoke}>Revoke</button></td>
    </tr>
  );
}

export function Pair() {
  const { user, loading } = useSession();
  const eligible = canDecide(user);

  const [raw, setRaw] = useState('');
  const [linked, setLinked] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [rows, setRows] = useState<TokenInfo[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const list = await tokens.list();
      setRows(list);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Could not load linked devices');
    }
  }, []);

  useEffect(() => {
    if (eligible) void refresh();
  }, [eligible, refresh]);

  const handleChange = (value: string) => {
    setRaw(stripCode(value));
    setLinked(false);
    setConfirmError(null);
  };

  const code = formatCode(raw);
  const ready = raw.length === CODE_LENGTH;

  const handleConfirm = async () => {
    setBusy(true);
    setConfirmError(null);
    try {
      await pair.approve(code);
      setLinked(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setConfirmError('That code was not recognised. Codes expire after 10 minutes.');
      } else {
        setConfirmError(err instanceof ApiError ? err.message : 'Could not link this device');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (t: TokenInfo) => {
    if (!window.confirm(`Revoke "${t.label}"?`)) return;
    try {
      await tokens.remove(t.id);
      await refresh();
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Could not revoke this device');
    }
  };

  if (loading) return <div className="app"><Spinner label="Checking your session" /></div>;
  if (!eligible) return <Navigate to="/projects" replace />;

  return (
    <div className="app">
      <Header crumbs={[{ label: 'Projects', to: '/projects' }, { label: 'Link InDesign' }]} />
      <main className="main">
        <div className="page pair" style={{ maxWidth: 640 }}>
          <div className="page-head"><h1>Link InDesign</h1></div>

          <div className="card" style={{ padding: 16, marginBottom: 20 }}>
            <div className="field">
              <label className="label" htmlFor="pair-code">Pairing code</label>
              <input
                id="pair-code"
                className="input mono"
                value={code}
                onChange={(e) => handleChange(e.target.value)}
                placeholder="XXXX-XXXX"
                autoComplete="off"
              />
            </div>

            {ready && !linked && (
              <div className="card" style={{ marginTop: 16 }}>
                <p>Link InDesign on this computer to your account?</p>
                <p className="mono">{code}</p>
                <button className="btn primary" onClick={() => void handleConfirm()} disabled={busy}>
                  {busy ? 'Linking' : 'Confirm'}
                </button>
              </div>
            )}

            {ready && linked && <p>Linked. Go back to InDesign.</p>}

            {confirmError && <p className="error-text" role="alert" style={{ marginTop: 12 }}>{confirmError}</p>}
          </div>

          <div className="page-head"><h2>Linked devices</h2></div>
          {loadError && <p className="error-text" role="alert">{loadError}</p>}
          {!loadError && rows === null && <Spinner label="Loading linked devices" />}
          {!loadError && rows && rows.length === 0 && <EmptyState title="No linked devices">Pair InDesign above.</EmptyState>}
          {!loadError && rows && rows.length > 0 && (
            <div className="card" style={{ overflow: 'auto' }}>
              <table>
                <thead><tr><th>Device</th><th>Created</th><th>Last used</th><th>Expires</th><th></th></tr></thead>
                <tbody>
                  {rows.map((t) => (
                    <DeviceRow key={t.id} t={t} onRevoke={() => void handleRevoke(t)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
